import React, { useEffect, useState } from 'react';
import { getCandidatureById, updateCandidature } from './api';
import { toast } from './Toast';

const emptyPoste = {
  plomberie: false,
  peinture: false,
  electricite: false,
  froidClimatisation: false,
  autre: false,
  autreTexte: '',
};

function toForm(submission) {
  return {
    nom: submission.nom || '',
    prenom: submission.prenom || '',
    telephone: submission.telephone || '',
    dateNaissance: submission.dateNaissance || '',
    ville: submission.ville || '',
    quartier: submission.quartier || '',
    niveauScolarisation: submission.niveauScolarisation || '',
    formationProfessionnelle: submission.formationProfessionnelle || '',
    anneesExperience: submission.anneesExperience || '',
    niveauCompetence: submission.niveauCompetence || '',
    lecturePlans: submission.lecturePlans || '',
    capaciteInstructions: submission.capaciteInstructions || '',
    materielTravail: submission.materielTravail || '',
    disponibiliteCommencer: submission.disponibiliteCommencer || '',
    smartphone: submission.smartphone || '',
    accesInternetMobile: submission.accesInternetMobile || '',
    moyenDeplacement: submission.moyenDeplacement || '',
    moyenDeplacementAutre: submission.moyenDeplacementAutre || '',
    posteSouhaite: { ...emptyPoste, ...(submission.posteSouhaite || {}) },
  };
}

export default function EditCandidatureModal({ submission, onClose, onSaved }) {
  const id = submission._id || submission.id;
  const [form, setForm] = useState(() => toForm(submission));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fresh = await getCandidatureById(id);
        if (!cancelled) setForm(toForm(fresh));
      } catch (err) {
        if (!cancelled) {
          toast(err.message || 'Chargement détail échoué — données liste', 'warning');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const togglePoste = (key) => {
    setForm((f) => ({
      ...f,
      posteSouhaite: { ...f.posteSouhaite, [key]: !f.posteSouhaite[key] },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nom: form.nom,
        prenom: form.prenom,
        telephone: form.telephone,
        dateNaissance: form.dateNaissance,
        ville: form.ville,
        quartier: form.quartier,
        niveauScolarisation: form.niveauScolarisation,
        formationProfessionnelle: form.formationProfessionnelle,
        anneesExperience: form.anneesExperience,
        niveauCompetence: form.niveauCompetence,
        lecturePlans: form.lecturePlans,
        capaciteInstructions: form.capaciteInstructions,
        materielTravail: form.materielTravail,
        disponibiliteCommencer: form.disponibiliteCommencer,
        smartphone: form.smartphone,
        accesInternetMobile: form.accesInternetMobile,
        moyenDeplacement: form.moyenDeplacement,
        moyenDeplacementAutre: form.moyenDeplacementAutre || '',
        posteSouhaite: {
          plomberie: !!form.posteSouhaite.plomberie,
          peinture: !!form.posteSouhaite.peinture,
          electricite: !!form.posteSouhaite.electricite,
          froidClimatisation: !!form.posteSouhaite.froidClimatisation,
          autre: !!form.posteSouhaite.autre,
          autreTexte: form.posteSouhaite.autreTexte || '',
        },
      };
      const updated = await updateCandidature(id, payload);
      toast('Candidature mise à jour', 'success');
      onSaved(updated);
      onClose();
    } catch (err) {
      toast(err.message || 'Erreur de mise à jour', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-xl font-semibold text-gray-800">Modifier la candidature</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 px-2">
            Fermer
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement…</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ['prenom', 'Prénom'],
                ['nom', 'Nom'],
                ['telephone', 'Téléphone'],
                ['dateNaissance', 'Date de naissance'],
                ['ville', 'Ville'],
                ['quartier', 'Quartier'],
                ['niveauScolarisation', 'Niveau de scolarisation'],
                ['formationProfessionnelle', 'Formation'],
                ['anneesExperience', "Années d'expérience"],
                ['niveauCompetence', 'Niveau de compétence'],
                ['lecturePlans', 'Lecture de plans'],
                ['capaciteInstructions', 'Capacité instructions'],
                ['materielTravail', 'Matériel'],
                ['disponibiliteCommencer', 'Disponibilité'],
                ['smartphone', 'Smartphone'],
                ['accesInternetMobile', 'Internet mobile'],
                ['moyenDeplacement', 'Moyen de déplacement'],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={form[key]}
                    onChange={(e) => setField(key, e.target.value)}
                    required={['nom', 'prenom', 'telephone'].includes(key)}
                  />
                </div>
              ))}
            </div>

            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">Postes souhaités</p>
              <div className="flex flex-wrap gap-3">
                {['plomberie', 'peinture', 'electricite', 'froidClimatisation', 'autre'].map((key) => (
                  <label key={key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(form.posteSouhaite[key])}
                      onChange={() => togglePoste(key)}
                    />
                    {key}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
